import React, { useState, useEffect } from 'react'
import contentService from '../services/contentService'
import Loading from '../components/Loading'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt  = n => (n == null ? '—' : Number(n).toLocaleString('es-CL'))
const fmtK = n => (n == null ? '—' : Number(n).toLocaleString('es-CL'))
const fmtDate = s => {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })
}
const fmtSec = s => {
  if (!s) return '—'
  const sec = Math.round(s)
  if (sec < 60) return `${sec}s`
  return `${Math.floor(sec/60)}m ${sec%60}s`
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner({ size = 28 }) {
  return (
    <div style={{
      width: size, height: size,
      border: `3px solid var(--g-border)`,
      borderTopColor: 'var(--g-lima)',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      display: 'inline-block',
    }} />
  )
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, color = 'var(--g-lima)', sub }) {
  return (
    <div className="g-card" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
          <p style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 800, color: 'var(--g-dark)', lineHeight: 1.1 }}>{value}</p>
          {sub && <p style={{ margin: '2px 0 0', fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)' }}>{sub}</p>}
        </div>
        {icon && (
          <div style={{ width: 36, height: 36, borderRadius: 'var(--g-radius-md)', background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className={`bi ${icon}`} style={{ color, fontSize: 16 }}></i>
          </div>
        )}
      </div>
    </div>
  )
}


// ── Post Card IG ──────────────────────────────────────────────────────────────
function IGPostCard({ post }) {
  const isReel = post.media_product_type === 'REELS'
  const isVid  = post.media_type === 'VIDEO'
  const thumb  = post.thumbnail_url || post.media_url

  return (
    <div className="g-card" style={{ overflow: 'hidden' }}>
      {/* Thumbnail */}
      <div style={{ position: 'relative', aspectRatio: '1/1', background: '#f3f4f6', overflow: 'hidden' }}>
        {thumb ? (
          <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="bi bi-image" style={{ fontSize: 32, opacity: 0.2 }}></i>
          </div>
        )}
        {/* Badge tipo */}
        <span style={{
          position: 'absolute', top: 8, left: 8,
          padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
          background: isReel ? '#8b5cf6' : isVid ? '#3b82f6' : 'rgba(0,0,0,0.5)',
          color: '#fff',
        }}>
          {isReel ? 'REEL' : isVid ? 'VIDEO' : 'POST'}
        </span>
        {/* Badge collab */}
        {post.is_collab && (
          <span style={{
            position: 'absolute', top: 8, right: 8,
            padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700,
            background: '#f97316', color: '#fff',
          }}>
            COLLAB
            {post.collab_username ? ` @${post.collab_username}` : ''}
          </span>
        )}
        {/* Fecha */}
        <span style={{
          position: 'absolute', bottom: 8, right: 8,
          padding: '2px 6px', borderRadius: 6, fontSize: 10,
          background: 'rgba(0,0,0,0.55)', color: '#fff',
        }}>
          {fmtDate(post.timestamp)}
        </span>
      </div>

      {/* Caption */}
      <div style={{ padding: '10px 12px 0' }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--g-text-sec)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
          {post.caption || <em style={{ opacity: 0.4 }}>Sin descripción</em>}
        </p>
      </div>

      {/* Métricas */}
      <div style={{ padding: '10px 12px 12px' }}>
        {/* Métricas de alcance/impresiones */}
        {[
          { icon: 'bi-broadcast', label: 'Alcance', val: post.reach },
          ...(isReel ? [{ icon: 'bi-eye-fill', label: 'Plays', val: post.plays }]
            : isVid  ? [{ icon: 'bi-eye-fill', label: 'Views', val: post.views }]
            : []),
          { icon: 'bi-hand-thumbs-up-fill', label: 'Interacciones', val: post.total_interactions },
          { icon: 'bi-bookmark-fill', label: 'Guardados', val: post.saved },
          { icon: 'bi-share-fill', label: 'Compartidos', val: post.shares },
          { icon: 'bi-heart-fill', label: 'Likes', val: post.likes ?? post.like_count },
          { icon: 'bi-chat-fill', label: 'Comentarios', val: post.comments ?? post.comments_count },
          ...(isReel ? [
            { icon: 'bi-stopwatch-fill', label: 'Avg watch', val: post.ig_reels_avg_watch_time != null ? fmtSec(post.ig_reels_avg_watch_time) : null, raw: true },
            { icon: 'bi-film', label: 'Tiempo total', val: post.ig_reels_video_view_total_time != null ? fmtSec(post.ig_reels_video_view_total_time) : null, raw: true },
          ] : []),
        ].filter(m => m.val != null && m.val !== '').map(m => (
          <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <i className={`bi ${m.icon}`} style={{ fontSize: 11, color: 'var(--g-text-muted)', flexShrink: 0 }}></i>
            <span style={{ fontSize: 11, color: 'var(--g-text-muted)' }}>{m.label}</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: 'var(--g-dark)' }}>
              {m.raw ? m.val : fmtK(m.val)}
            </span>
          </div>
        ))}
        {/* Métricas totales incl. patrocinado */}
        {(post.total_like_count != null || post.total_comments_count != null || post.total_views_count != null) && (
          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--g-border)' }}>
            <p style={{ margin: '0 0 4px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#f97316' }}>
              Total incl. patrocinado
            </p>
            {[
              { icon: 'bi-eye-fill', label: 'Vistas totales', val: post.total_views_count },
              { icon: 'bi-heart-fill', label: 'Likes totales', val: post.total_like_count },
              { icon: 'bi-chat-fill', label: 'Comentarios totales', val: post.total_comments_count },
            ].filter(m => m.val != null).map(m => (
              <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                <i className={`bi ${m.icon}`} style={{ fontSize: 11, color: '#f97316', flexShrink: 0 }}></i>
                <span style={{ fontSize: 11, color: 'var(--g-text-muted)' }}>{m.label}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#f97316' }}>{fmtK(m.val)}</span>
              </div>
            ))}
          </div>
        )}
        {/* Si no hay ninguna métrica de insights */}
        {post.reach == null && post.plays == null && post.views == null && post.profile_visits == null && post.total_interactions == null && (
          <p style={{ margin: 0, fontSize: 11, color: 'var(--g-text-muted)', fontStyle: 'italic' }}>
            {post._insights_error
              ? `API: ${post._insights_error}`
              : 'Métricas no disponibles para este post'}
          </p>
        )}
      </div>

      {/* Link */}
      {post.permalink && (
        <div style={{ borderTop: '1px solid var(--g-border)', padding: '8px 12px' }}>
          <a href={post.permalink} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: 'var(--g-teal)', textDecoration: 'none', fontWeight: 600 }}>
            <i className="bi bi-box-arrow-up-right" style={{ marginRight: 4 }}></i>Ver en Instagram
          </a>
        </div>
      )}
    </div>
  )
}

// ── Post Card FB ──────────────────────────────────────────────────────────────
function FBPostCard({ post }) {
  return (
    <div className="g-card" style={{ overflow: 'hidden' }}>
      {post.picture && (
        <div style={{ aspectRatio: '16/9', overflow: 'hidden', background: '#f3f4f6' }}>
          <img src={post.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none' }} />
        </div>
      )}
      <div style={{ padding: '12px 14px 0' }}>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--g-text-muted)' }}>{fmtDate(post.timestamp)}</p>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--g-text-sec)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
          {post.message || <em style={{ opacity: 0.4 }}>Sin texto</em>}
        </p>
      </div>

      <div style={{ padding: '10px 14px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
        {[
          { icon: 'bi-hand-thumbs-up-fill', label: 'Reacciones', val: post.likes },
          { icon: 'bi-chat-fill', label: 'Comentarios', val: post.comments },
          { icon: 'bi-share-fill', label: 'Compartidos', val: post.shares },
          { icon: 'bi-broadcast', label: 'Alcance', val: post.post_impressions_unique },
          { icon: 'bi-eye-fill', label: 'Impresiones', val: post.post_impressions },
          { icon: 'bi-cursor-fill', label: 'Clicks', val: post.post_clicks },
          { icon: 'bi-people-fill', label: 'Engaged', val: post.post_engaged_users },
        ].filter(m => m.val != null && m.val !== '' && m.val !== 0).map(m => (
          <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <i className={`bi ${m.icon}`} style={{ fontSize: 11, color: 'var(--g-text-muted)', flexShrink: 0 }}></i>
            <span style={{ fontSize: 11, color: 'var(--g-text-muted)' }}>{m.label}</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: 'var(--g-dark)' }}>{fmtK(m.val)}</span>
          </div>
        ))}
      </div>

      {post.permalink && (
        <div style={{ borderTop: '1px solid var(--g-border)', padding: '8px 14px' }}>
          <a href={post.permalink} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: '#1877F2', textDecoration: 'none', fontWeight: 600 }}>
            <i className="bi bi-box-arrow-up-right" style={{ marginRight: 4 }}></i>Ver en Facebook
          </a>
        </div>
      )}
    </div>
  )
}

// ── Demographics bar ──────────────────────────────────────────────────────────
function DemoBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
        <span style={{ color: 'var(--g-text-sec)', fontWeight: 600 }}>{label}</span>
        <span style={{ color: 'var(--g-text-muted)' }}>{fmt(value)} ({pct}%)</span>
      </div>
      <div style={{ height: 6, background: 'var(--g-fondo)', borderRadius: 4, overflow: 'hidden', border: '1px solid var(--g-border)' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color || 'var(--g-lima)', borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
    </div>
  )
}


// ── IG Tab ────────────────────────────────────────────────────────────────────
function IGTab({ igUserId }) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [limit, setLimit]     = useState(30)
  const [saving, setSaving]   = useState(false)
  const [saveMsg, setSaveMsg] = useState(null)  // { ok, text }

  const handleGrabar = async () => {
    if (!data) return
    setSaving(true)
    setSaveMsg(null)
    try {
      const res = await contentService.grabarIgSnapshot(igUserId, {
        perfil:           data.perfil,
        account_insights: data.account_insights,
        posts:            data.posts,
        stories:          data.stories,
      })
      const d = res.data
      setSaveMsg({ ok: true, text: `✓ Grabado: ${d.posts_grabados} posts y ${d.stories_grabadas} stories para el ${d.fecha}` })
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.response?.data?.error || 'Error al grabar' })
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    setError(null)
    contentService.getIgFullData(igUserId, limit)
      .then(({ data: d }) => setData(d))
      .catch(e => setError(e?.response?.data?.error || 'Error al cargar datos'))
      .finally(() => setLoading(false))
  }, [igUserId, limit])

  if (loading) return (
    <Loading fullScreen={false} mensaje="Cargando" />
  )
  if (error) return (
    <div style={{ padding: 24, color: '#dc2626', background: '#fef2f2', borderRadius: 'var(--g-radius-md)', border: '1px solid #fca5a5' }}>
      <i className="bi bi-exclamation-circle-fill" style={{ marginRight: 8 }}></i>{error}
    </div>
  )
  if (!data) return null

  const { perfil, posts, stories = [], account_insights = {}, demographics = {} } = data
  const ins = account_insights['days_28'] || {}

  const reachedDemo = demographics?.reached_audience_demographics?.total_value?.breakdowns || []
  const followerDemo = demographics?.follower_demographics?.total_value?.breakdowns || []

  // Helper para extraer breakdown por dimensión
  const getBreakdown = (breakdowns, dimName) =>
    (breakdowns.find(b => b.dimension_keys?.includes(dimName))?.results || [])
      .sort((a, b) => (b.value || 0) - (a.value || 0))
      .slice(0, 8)

  const reachedAge    = getBreakdown(reachedDemo, 'age')
  const reachedGender = getBreakdown(reachedDemo, 'gender')
  const reachedCountry = getBreakdown(reachedDemo, 'country')
  const followerAge   = getBreakdown(followerDemo, 'age')
  const followerCountry = getBreakdown(followerDemo, 'country')

  const totalReachedAge    = reachedAge.reduce((s, r) => s + (r.value || 0), 0)
  const totalFollowerAge   = followerAge.reduce((s, r) => s + (r.value || 0), 0)
  const totalReachedCountry = reachedCountry.reduce((s, r) => s + (r.value || 0), 0)
  const totalFollowerCountry = followerCountry.reduce((s, r) => s + (r.value || 0), 0)

  const DEMO_COLORS = ['var(--g-lima)', 'var(--g-teal)', '#8b5cf6', '#f97316', '#3b82f6', '#ec4899', '#10b981', '#f59e0b']

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Botón GRABAR */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={handleGrabar}
          disabled={saving}
          style={{
            padding: '10px 24px',
            borderRadius: 'var(--g-radius-md)',
            border: 'none',
            background: saving ? 'var(--g-border)' : 'var(--g-lima)',
            color: saving ? 'var(--g-text-muted)' : '#000',
            fontWeight: 800,
            fontSize: 14,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            letterSpacing: '0.05em',
          }}
        >
          {saving
            ? <><Spinner size={14} /> Grabando...</>
            : <><i className="bi bi-cloud-arrow-up-fill" /> GRABAR SNAPSHOT</>
          }
        </button>
        {saveMsg && (
          <span style={{
            fontSize: 13, fontWeight: 600,
            color: saveMsg.ok ? '#16a34a' : '#dc2626',
            background: saveMsg.ok ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${saveMsg.ok ? '#86efac' : '#fca5a5'}`,
            borderRadius: 'var(--g-radius-sm)',
            padding: '6px 12px',
          }}>
            {saveMsg.text}
          </span>
        )}
      </div>

      {/* Perfil header */}
      <div className="g-card">
        <div className="g-card-body" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {perfil.profile_pic_url ? (
            <img src={perfil.profile_pic_url} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              onError={e => { e.target.style.display = 'none' }} />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#8b5cf6,#ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="bi bi-instagram" style={{ color: '#fff', fontSize: 28 }}></i>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 'var(--g-font-size-xl)', color: 'var(--g-dark)' }}>@{perfil.username}</p>
            {perfil.nombre && <p style={{ margin: '2px 0 0', fontSize: 'var(--g-font-size-sm)', color: 'var(--g-text-muted)' }}>{perfil.nombre}</p>}
            {perfil.bio && <p style={{ margin: '4px 0 0', fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-sec)', maxWidth: 480 }}>{perfil.bio}</p>}
          </div>
          <div style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
            {[
              { label: 'Seguidores', val: fmtK(perfil.seguidores) },
              { label: 'Siguiendo', val: fmtK(perfil.siguiendo) },
              { label: 'Posts', val: fmtK(perfil.media_count) },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontWeight: 800, fontSize: 18, color: 'var(--g-dark)' }}>{s.val}</p>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--g-text-muted)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Account Insights */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <p className="g-section-title" style={{ margin: 0 }}>Métricas de la cuenta</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {[
            { label: 'Alcance', val: fmtK(ins.reach), icon: 'bi-broadcast', color: '#8b5cf6' },
            { label: 'Impresiones', val: fmtK(ins.impressions), icon: 'bi-eye-fill', color: '#3b82f6' },
            { label: 'Cuentas engaged', val: fmtK(ins.accounts_engaged), icon: 'bi-people-fill', color: 'var(--g-teal)' },
            { label: 'Visitas perfil', val: fmtK(ins.profile_views), icon: 'bi-person-badge-fill', color: '#f97316' },
            { label: 'Clics website', val: fmtK(ins.website_clicks), icon: 'bi-cursor-fill', color: '#ec4899' },
            { label: 'Total interacciones', val: fmtK(ins.total_interactions), icon: 'bi-hand-thumbs-up-fill', color: 'var(--g-lima)' },
            { label: 'Likes', val: fmtK(ins.likes), icon: 'bi-heart-fill', color: '#ef4444' },
            { label: 'Comentarios', val: fmtK(ins.comments), icon: 'bi-chat-fill', color: '#6366f1' },
            { label: 'Compartidos', val: fmtK(ins.shares), icon: 'bi-share-fill', color: '#14b8a6' },
            { label: 'Guardados', val: fmtK(ins.saves), icon: 'bi-bookmark-fill', color: '#f59e0b' },
            { label: 'Respuestas', val: fmtK(ins.replies), icon: 'bi-reply-fill', color: '#a78bfa' },
            { label: 'Seguidores', val: fmtK(ins.follower_count), icon: 'bi-person-plus-fill', color: 'var(--g-dark)' },
          ].filter(m => m.val && m.val !== '—').map(m => (
            <StatCard key={m.label} label={m.label} value={m.val} icon={m.icon} color={m.color} />
          ))}
        </div>
      </div>

      {/* Demographics */}
      {(reachedAge.length > 0 || reachedCountry.length > 0) && (
        <div>
          <p className="g-section-title" style={{ margin: '0 0 12px' }}>Demografía (últimos 30 días)</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>

            {reachedGender.length > 0 && (
              <div className="g-card">
                <div className="g-card-header"><p className="g-card-header-title">Alcance por género</p></div>
                <div className="g-card-body">
                  {reachedGender.map((r, i) => (
                    <DemoBar key={i} label={r.dimension_values?.[0] || '?'} value={r.value} total={reachedGender.reduce((s,x)=>s+(x.value||0),0)} color={DEMO_COLORS[i]} />
                  ))}
                </div>
              </div>
            )}

            {reachedAge.length > 0 && (
              <div className="g-card">
                <div className="g-card-header"><p className="g-card-header-title">Alcance por edad</p></div>
                <div className="g-card-body">
                  {reachedAge.map((r, i) => (
                    <DemoBar key={i} label={r.dimension_values?.[0] || '?'} value={r.value} total={totalReachedAge} color={DEMO_COLORS[i]} />
                  ))}
                </div>
              </div>
            )}

            {reachedCountry.length > 0 && (
              <div className="g-card">
                <div className="g-card-header"><p className="g-card-header-title">Alcance por país</p></div>
                <div className="g-card-body">
                  {reachedCountry.map((r, i) => (
                    <DemoBar key={i} label={r.dimension_values?.[0] || '?'} value={r.value} total={totalReachedCountry} color={DEMO_COLORS[i]} />
                  ))}
                </div>
              </div>
            )}

            {followerAge.length > 0 && (
              <div className="g-card">
                <div className="g-card-header"><p className="g-card-header-title">Seguidores por edad</p></div>
                <div className="g-card-body">
                  {followerAge.map((r, i) => (
                    <DemoBar key={i} label={r.dimension_values?.[0] || '?'} value={r.value} total={totalFollowerAge} color={DEMO_COLORS[i]} />
                  ))}
                </div>
              </div>
            )}

            {followerCountry.length > 0 && (
              <div className="g-card">
                <div className="g-card-header"><p className="g-card-header-title">Seguidores por país</p></div>
                <div className="g-card-body">
                  {followerCountry.map((r, i) => (
                    <DemoBar key={i} label={r.dimension_values?.[0] || '?'} value={r.value} total={totalFollowerCountry} color={DEMO_COLORS[i]} />
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Stories */}
      {stories.length > 0 && (
        <div>
          <p className="g-section-title" style={{ margin: '0 0 12px' }}>
            Stories activas ({stories.length})
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {stories.filter(s => s.media_url).map(s => (
              <div key={s.id} className="g-card" style={{ overflow: 'hidden' }}>
                {/* Thumbnail story (ratio 9:16) */}
                <div style={{ position: 'relative', aspectRatio: '9/16', background: '#1a1a2e', overflow: 'hidden', maxHeight: 200 }}>
                  {s.media_type === 'VIDEO'
                    ? <video src={s.media_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                    : <img src={s.media_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  }
                  <span style={{
                    position: 'absolute', top: 6, left: 6,
                    padding: '2px 7px', borderRadius: 20, fontSize: 9, fontWeight: 700,
                    background: 'linear-gradient(135deg,#8b5cf6,#ec4899)', color: '#fff',
                  }}>STORY</span>
                  <span style={{
                    position: 'absolute', bottom: 6, right: 6,
                    padding: '2px 5px', borderRadius: 5, fontSize: 9,
                    background: 'rgba(0,0,0,0.6)', color: '#fff',
                  }}>{fmtDate(s.timestamp)}</span>
                </div>

                {/* Métricas story */}
                <div style={{ padding: '10px 12px 12px' }}>
                  {[
                    { icon: 'bi-broadcast',         label: 'Alcance',       val: s.reach },
                    { icon: 'bi-heart-fill',        label: 'Me gusta',      val: s.likes },
                    { icon: 'bi-reply-fill',        label: 'Respuestas',    val: s.replies },
                    { icon: 'bi-share-fill',        label: 'Compartidos',   val: s.shares },
                    { icon: 'bi-link-45deg',        label: 'Clics enlace',  val: s.link_clicks },
                    { icon: 'bi-compass-fill',      label: 'Navegación',    val: s.navigation },
                    { icon: 'bi-people-fill',       label: 'Interacciones', val: s.total_interactions },
                    { icon: 'bi-person-plus-fill',  label: 'Nuevos seguid.',val: s.follows },
                    { icon: 'bi-person-lines-fill', label: 'Visitas perfil',val: s.profile_visits },
                  ].filter(m => m.val != null).map(m => (
                    <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
                      <i className={`bi ${m.icon}`} style={{ fontSize: 10, color: 'var(--g-text-muted)', flexShrink: 0 }}></i>
                      <span style={{ fontSize: 10, color: 'var(--g-text-muted)' }}>{m.label}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: 'var(--g-dark)' }}>{fmtK(m.val)}</span>
                    </div>
                  ))}
                  {s.reach == null && s.replies == null && s.navigation == null && s.total_interactions == null && s.follows == null && s.profile_visits == null && s.likes == null && s.shares == null && s.link_clicks == null && (
                    <p style={{ margin: 0, fontSize: 10, color: 'var(--g-text-muted)', fontStyle: 'italic' }}>
                      Métricas no disponibles
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Posts */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <p className="g-section-title" style={{ margin: 0 }}>Últimos {posts.length} posts</p>
          <select
            value={limit}
            onChange={e => setLimit(Number(e.target.value))}
            disabled={loading}
            style={{ padding: '4px 10px', borderRadius: 'var(--g-radius-sm)', border: '1px solid var(--g-border)', background: 'var(--g-surface)', color: 'var(--g-text)', fontSize: 13, cursor: 'pointer' }}
          >
            {[30, 60, 100, 200, 300].map(n => (
              <option key={n} value={n}>{n} posts</option>
            ))}
          </select>
        </div>

        {/* KPIs acumulados de los posts */}
        {(() => {
          const sum = (key) => posts.reduce((s, p) => s + (Number(p[key]) || 0), 0)
          const feedPosts = posts.filter(p => p.media_product_type !== 'STORY')
          const reels     = feedPosts.filter(p => p.media_product_type === 'REELS')
          const totalReach         = sum('reach')
          const totalLikes         = sum('likes')
          const totalComments      = sum('comments')
          const totalShares        = sum('shares')
          const totalSaved         = sum('saved')
          const totalInteractions  = sum('total_interactions')
          const totalViews         = sum('views')
          // ER promedio = total_interactions / total_reach * 100 (solo posts con reach > 0)
          const erList = feedPosts.filter(p => p.reach > 0).map(p => ((p.total_interactions || 0) / p.reach) * 100)
          const avgEr  = erList.length ? erList.reduce((a, b) => a + b, 0) / erList.length : null
          const kpis = [
            { label: 'Posts',         val: feedPosts.length,        color: 'var(--g-dark)' },
            { label: 'Alcance total', val: fmt(totalReach),          color: '#8b5cf6' },
            { label: 'Likes',         val: fmt(totalLikes),          color: '#ef4444' },
            { label: 'Comentarios',   val: fmt(totalComments),       color: '#6366f1' },
            { label: 'Compartidos',   val: fmt(totalShares),         color: '#14b8a6' },
            { label: 'Guardados',     val: fmt(totalSaved),          color: '#f59e0b' },
            { label: 'Interacciones', val: fmt(totalInteractions),   color: 'var(--g-lima)' },
            { label: 'Vistas (reels)',val: fmt(totalViews),          color: '#3b82f6' },
            { label: 'ER promedio',   val: avgEr != null ? `${avgEr.toFixed(2)}%` : '—', color: '#10b981' },
            { label: 'Reels',         val: reels.length,             color: '#e1306c' },
          ]
          return (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
              {kpis.map(k => (
                <div key={k.label} style={{
                  background: 'var(--g-surface)',
                  border: '1px solid var(--g-border)',
                  borderRadius: 14,
                  padding: '14px 16px 12px',
                  minWidth: 100,
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--g-text-secondary)', marginBottom: 4 }}>{k.label}</span>
                  <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: k.color }}>{k.val}</span>
                </div>
              ))}
            </div>
          )
        })()}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
          {posts.map(p => <IGPostCard key={p.id} post={p} />)}
        </div>
      </div>
    </div>
  )
}

// ── FB Tab ────────────────────────────────────────────────────────────────────
function FBTab({ pageId }) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [period]              = useState('days_28')
  const [saving, setSaving]   = useState(false)
  const [saveMsg, setSaveMsg] = useState(null)

  const handleGrabar = async () => {
    if (!data) return
    setSaving(true)
    setSaveMsg(null)
    try {
      const res = await contentService.grabarFbSnapshot(pageId, {
        perfil:        data.perfil,
        page_insights: data.page_insights,
        posts:         data.posts,
        demographics:  data.demographics,
      })
      const d = res.data
      setSaveMsg({ ok: true, text: `✓ Grabado: ${d.posts_grabados} posts para el ${d.fecha}` })
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.response?.data?.error || 'Error al grabar' })
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    setError(null)
    contentService.getFbFullData(pageId, 30)
      .then(({ data: d }) => setData(d))
      .catch(e => setError(e?.response?.data?.error || 'Error al cargar datos'))
      .finally(() => setLoading(false))
  }, [pageId])

  if (loading) return (
    <Loading fullScreen={false} mensaje="Cargando" />
  )
  if (error) return (
    <div style={{ padding: 24, color: '#dc2626', background: '#fef2f2', borderRadius: 'var(--g-radius-md)', border: '1px solid #fca5a5' }}>
      <i className="bi bi-exclamation-circle-fill" style={{ marginRight: 8 }}></i>{error}
    </div>
  )
  if (!data) return null

  const { perfil, posts, page_insights, demographics } = data
  const ins = page_insights[period] || {}

  const DEMO_COLORS = ['var(--g-lima)', 'var(--g-teal)', '#8b5cf6', '#f97316', '#3b82f6', '#ec4899', '#10b981', '#f59e0b']

  // Fan demographics (género/edad)
  const fanGenderAge = demographics?.fan_gender_age || {}
  const fanAgeGroups = {}
  Object.entries(fanGenderAge).forEach(([key, val]) => {
    const [, age] = key.split('.')
    fanAgeGroups[age] = (fanAgeGroups[age] || 0) + val
  })
  const fanAgeArr = Object.entries(fanAgeGroups).sort((a,b)=>b[1]-a[1]).slice(0, 8)
  const totalFanAge = fanAgeArr.reduce((s,[,v])=>s+v, 0)

  const fanCountry = demographics?.fan_country || {}
  const fanCountryArr = Object.entries(fanCountry).sort((a,b)=>b[1]-a[1]).slice(0, 8)
  const totalFanCountry = fanCountryArr.reduce((s,[,v])=>s+v, 0)

  const fanCity = demographics?.fan_city || {}
  const fanCityArr = Object.entries(fanCity).sort((a,b)=>b[1]-a[1]).slice(0, 8)
  const totalFanCity = fanCityArr.reduce((s,[,v])=>s+v, 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Botón GRABAR FB */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={handleGrabar}
          disabled={saving}
          style={{
            padding: '10px 24px',
            borderRadius: 'var(--g-radius-md)',
            border: 'none',
            background: saving ? 'var(--g-border)' : '#1877F2',
            color: saving ? 'var(--g-text-muted)' : '#fff',
            fontWeight: 800,
            fontSize: 14,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            letterSpacing: '0.05em',
          }}
        >
          {saving
            ? <><Spinner size={14} /> Grabando...</>
            : <><i className="bi bi-cloud-arrow-up-fill" /> GRABAR SNAPSHOT</>
          }
        </button>
        {saveMsg && (
          <span style={{
            fontSize: 13, fontWeight: 600,
            color: saveMsg.ok ? '#16a34a' : '#dc2626',
            background: saveMsg.ok ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${saveMsg.ok ? '#86efac' : '#fca5a5'}`,
            borderRadius: 'var(--g-radius-sm)',
            padding: '6px 12px',
          }}>
            {saveMsg.text}
          </span>
        )}
      </div>

      {/* Perfil header */}
      <div className="g-card">
        <div className="g-card-body" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {perfil.picture_url ? (
            <img src={perfil.picture_url} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              onError={e => { e.target.style.display = 'none' }} />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#1877F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="bi bi-facebook" style={{ color: '#fff', fontSize: 28 }}></i>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 'var(--g-font-size-xl)', color: 'var(--g-dark)' }}>{perfil.nombre}</p>
            {perfil.categoria && <p style={{ margin: '2px 0 0', fontSize: 'var(--g-font-size-sm)', color: 'var(--g-text-muted)' }}>{perfil.categoria}</p>}
            {perfil.about && <p style={{ margin: '4px 0 0', fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-sec)', maxWidth: 480 }}>{perfil.about}</p>}
          </div>
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 22, color: 'var(--g-dark)' }}>{fmtK(perfil.fans)}</p>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--g-text-muted)' }}>Fans</p>
          </div>
        </div>
      </div>

      {/* Page Insights */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <p className="g-section-title" style={{ margin: 0 }}>Métricas de la página</p>
        </div>

        {/* Grupos de métricas */}
        {[
          {
            title: 'Alcance e Impresiones',
            items: [
              { label: 'Impresiones', val: ins.page_impressions, icon: 'bi-eye-fill', color: '#3b82f6' },
              { label: 'Alcance único', val: ins.page_impressions_unique, icon: 'bi-broadcast', color: '#8b5cf6' },
              { label: 'Impresiones pagadas', val: ins.page_impressions_paid, icon: 'bi-megaphone-fill', color: '#f97316' },
              { label: 'Impresiones orgánicas', val: ins.page_impressions_organic, icon: 'bi-tree-fill', color: 'var(--g-lima)' },
              { label: 'Impresiones virales', val: ins.page_impressions_viral, icon: 'bi-lightning-fill', color: '#ec4899' },
            ],
          },
          {
            title: 'Engagement',
            items: [
              { label: 'Usuarios engaged', val: ins.page_engaged_users, icon: 'bi-people-fill', color: 'var(--g-teal)' },
              { label: 'Interacciones en posts', val: ins.page_post_engagements, icon: 'bi-hand-thumbs-up-fill', color: 'var(--g-lima)' },
              { label: 'Consumos de contenido', val: ins.page_consumptions, icon: 'bi-cursor-fill', color: '#6366f1' },
              { label: 'Reacciones', val: ins.page_actions_post_reactions_total, icon: 'bi-emoji-smile-fill', color: '#f59e0b' },
            ],
          },
          {
            title: 'Fans',
            items: [
              { label: 'Total fans', val: ins.page_fans, icon: 'bi-star-fill', color: '#f59e0b' },
              { label: 'Fans ganados', val: ins.page_fan_adds, icon: 'bi-person-plus-fill', color: 'var(--g-lima)' },
              { label: 'Fans perdidos', val: ins.page_fan_removes, icon: 'bi-person-dash-fill', color: '#ef4444' },
            ],
          },
          {
            title: 'Videos',
            items: [
              { label: 'Views de video', val: ins.page_video_views, icon: 'bi-play-circle-fill', color: '#3b82f6' },
              { label: 'Views pagados', val: ins.page_video_views_paid, icon: 'bi-megaphone-fill', color: '#f97316' },
              { label: 'Views orgánicos', val: ins.page_video_views_organic, icon: 'bi-tree-fill', color: 'var(--g-lima)' },
              { label: 'Views completos (30s)', val: ins.page_video_complete_views_30s, icon: 'bi-check-circle-fill', color: 'var(--g-teal)' },
            ],
          },
          {
            title: 'Acciones en la página',
            items: [
              { label: 'Visitas totales', val: ins.page_views_total, icon: 'bi-eye-fill', color: '#6366f1' },
              { label: 'Visitas (logueados)', val: ins.page_views_logged_in_total, icon: 'bi-person-badge-fill', color: '#8b5cf6' },
            ],
          },
        ].map(group => {
          const visible = group.items.filter(m => m.val != null && m.val !== 0)
          if (!visible.length) return null
          return (
            <div key={group.title} style={{ marginBottom: 16 }}>
              <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--g-text-sec)' }}>{group.title}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                {visible.map(m => <StatCard key={m.label} label={m.label} value={fmtK(m.val)} icon={m.icon} color={m.color} />)}
              </div>
            </div>
          )
        })}
      </div>

      {/* Demographics */}
      {(fanAgeArr.length > 0 || fanCountryArr.length > 0) && (
        <div>
          <p className="g-section-title" style={{ margin: '0 0 12px' }}>Demografía de fans</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {fanAgeArr.length > 0 && (
              <div className="g-card">
                <div className="g-card-header"><p className="g-card-header-title">Edad de fans</p></div>
                <div className="g-card-body">
                  {fanAgeArr.map(([age, val], i) => (
                    <DemoBar key={age} label={age} value={val} total={totalFanAge} color={DEMO_COLORS[i]} />
                  ))}
                </div>
              </div>
            )}
            {fanCountryArr.length > 0 && (
              <div className="g-card">
                <div className="g-card-header"><p className="g-card-header-title">País de fans</p></div>
                <div className="g-card-body">
                  {fanCountryArr.map(([country, val], i) => (
                    <DemoBar key={country} label={country} value={val} total={totalFanCountry} color={DEMO_COLORS[i]} />
                  ))}
                </div>
              </div>
            )}
            {fanCityArr.length > 0 && (
              <div className="g-card">
                <div className="g-card-header"><p className="g-card-header-title">Ciudad de fans</p></div>
                <div className="g-card-body">
                  {fanCityArr.map(([city, val], i) => (
                    <DemoBar key={city} label={city} value={val} total={totalFanCity} color={DEMO_COLORS[i]} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Posts */}
      <div>
        <p className="g-section-title" style={{ margin: '0 0 12px' }}>Últimos {posts.length} posts</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {posts.map(p => <FBPostCard key={p.id} post={p} />)}
        </div>
      </div>
    </div>
  )
}

// ── Account Selector ──────────────────────────────────────────────────────────
function AccountSelector({ accounts, selected, onChange, type }) {
  if (!accounts || accounts.length <= 1) return null
  return (
    <select value={selected} onChange={e => onChange(e.target.value)}
      style={{
        padding: '6px 12px', borderRadius: 'var(--g-radius-md)', border: '1px solid var(--g-border)',
        fontSize: 'var(--g-font-size-sm)', fontWeight: 600, color: 'var(--g-dark)',
        background: 'var(--g-white)', cursor: 'pointer',
      }}>
      {accounts.map(a => (
        <option key={type === 'ig' ? a.ig_user_id : a.page_id} value={type === 'ig' ? a.ig_user_id : a.page_id}>
          {type === 'ig' ? `@${a.username}` : a.nombre}
        </option>
      ))}
    </select>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MetaAnalyticsPage() {
  const [tab, setTab]             = useState('ig')
  const [estado, setEstado]       = useState(null)
  const [estadoFB, setEstadoFB]   = useState(null)
  const [loadingEstado, setLoadingEstado] = useState(true)
  const [selIG, setSelIG]         = useState('')
  const [selFB, setSelFB]         = useState('')

  useEffect(() => {
    setLoadingEstado(true)
    Promise.all([
      contentService.getInstagramMetaEstado(),
      contentService.getFacebookMetaEstado(),
    ]).then(([{ data: ig }, { data: fb }]) => {
      setEstado(ig)
      setEstadoFB(fb)
      if (ig.cuentas?.[0]) setSelIG(ig.cuentas[0].ig_user_id)
      if (fb.paginas?.[0]) setSelFB(fb.paginas[0].page_id)
    }).catch(() => {}).finally(() => setLoadingEstado(false))
  }, [])

  const TABS = [
    { key: 'ig', label: 'Instagram', icon: 'bi-instagram', color: 'linear-gradient(135deg,#8b5cf6,#ec4899)' },
    { key: 'fb', label: 'Facebook', icon: 'bi-facebook', color: '#1877F2' },
  ]

  return (
    <div className="g-page">
      <div className="g-page-header">
        <div>
          <h1 className="g-page-title">
            <i className="bi bi-bar-chart-fill" style={{ marginRight: 10, color: 'var(--g-lima)' }}></i>
            Analytics Meta
          </h1>
          <p className="g-page-subtitle">Métricas completas de tus cuentas Instagram y Facebook conectadas.</p>
        </div>
        {/* Account selector */}
        {tab === 'ig' && estado?.cuentas?.length > 1 && (
          <AccountSelector accounts={estado.cuentas} selected={selIG} onChange={setSelIG} type="ig" />
        )}
        {tab === 'fb' && estadoFB?.paginas?.length > 1 && (
          <AccountSelector accounts={estadoFB.paginas} selected={selFB} onChange={setSelFB} type="fb" />
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--g-border)', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', border: 'none', cursor: 'pointer', background: 'none',
              fontWeight: 700, fontSize: 'var(--g-font-size-sm)',
              color: tab === t.key ? 'var(--g-dark)' : 'var(--g-text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--g-dark)' : '2px solid transparent',
              marginBottom: -2, transition: 'all 0.15s',
            }}>
            <div style={{
              width: 24, height: 24, borderRadius: 6,
              background: t.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className={`bi ${t.icon}`} style={{ color: '#fff', fontSize: 11 }}></i>
            </div>
            {t.label}
          </button>
        ))}
      </div>

      {loadingEstado ? (
        <Loading fullScreen={false} mensaje="Cargando" />
      ) : !estado?.conectado ? (
        <div className="g-card">
          <div className="g-empty">
            <i className="bi bi-plug-fill g-empty-icon"></i>
            <p className="g-empty-title">No hay cuenta Meta conectada</p>
            <p className="g-empty-desc">Ve a Configuración → Cuentas Conectadas para vincular tu cuenta de Instagram o Facebook.</p>
            <a href="/configuracion/cuentas-conectadas" className="g-btn g-btn-primary">
              <i className="bi bi-plug-fill"></i>Conectar cuenta
            </a>
          </div>
        </div>
      ) : (
        <>
          {tab === 'ig' && <IGTab igUserId={selIG} />}
          {tab === 'fb' && <FBTab pageId={selFB} />}
        </>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}
